import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import * as EqualizerActions from './equalizer.actions';
import { selectCustomPresets, selectEqualizerState } from './equalizer.selectors';
import { EqualizerState } from './equalizer.reducer';
import { EqualizerBand, EqualizerPreset } from './objects/media-player/equalizer-preset';
import { BrowserStorage } from './util/browser-storage';
import { EqualizerService } from './services/media-player/equalizer.service';

/** What we remember about the equalizer between visits. */
interface StoredEqualizerSettings {
  presetId: string;
  bands: EqualizerBand[];
  isEnabled: boolean;
}

@Injectable()
export class EqualizerEffects {
  private readonly STORAGE_KEY = 'bubie-equalizer-presets';
  private readonly SETTINGS_KEY = 'bubie-equalizer-settings';

  loadPresetsFromStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EqualizerActions.loadPresetsFromStorage),
      mergeMap(() => {
        // Custom presets first: the remembered selection may point at one of
        // them, and the reducer resolves it against the preset list.
        const actions: Action[] = [
          EqualizerActions.loadPresetsFromStorageSuccess({ presets: this.readCustomPresets() })
        ];

        const settings = this.readSettings();
        if (settings) {
          actions.push(EqualizerActions.restoreEqualizerSettings(settings));
        }

        return actions;
      })
    )
  );

  savePresetsToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EqualizerActions.createCustomPreset,
          EqualizerActions.deleteCustomPreset,
          EqualizerActions.updateCustomPreset
        ),
        withLatestFrom(this.store.select(selectCustomPresets)),
        tap(([_, customPresets]) => {
          BrowserStorage.setItem(this.STORAGE_KEY, JSON.stringify(customPresets));
        })
      ),
    { dispatch: false }
  );

  saveSettingsToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EqualizerActions.selectPreset,
          EqualizerActions.updateBand,
          EqualizerActions.enableEqualizer,
          EqualizerActions.disableEqualizer,
          // Both can change which preset is current, or its bands.
          EqualizerActions.deleteCustomPreset,
          EqualizerActions.updateCustomPreset
        ),
        withLatestFrom(this.store.select(selectEqualizerState)),
        tap(([_, state]) => {
          const settings: StoredEqualizerSettings = {
            presetId: state.currentPreset.id,
            bands: state.currentPreset.bands,
            isEnabled: state.isEnabled
          };
          BrowserStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));
        })
      ),
    { dispatch: false }
  );

  /**
   * Keeps the live audio graph in step with the store, wherever the change
   * came from. The graph itself is attached by `MediaPlayerService` on play,
   * so this is a no-op until something has played.
   */
  applyToAudio$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          EqualizerActions.restoreEqualizerSettings,
          EqualizerActions.selectPreset,
          EqualizerActions.updateBand,
          EqualizerActions.enableEqualizer,
          EqualizerActions.disableEqualizer,
          EqualizerActions.deleteCustomPreset,
          EqualizerActions.updateCustomPreset
        ),
        withLatestFrom(this.store.select(selectEqualizerState)),
        tap(([_, state]) => {
          this.equalizerService.applyState(state.currentPreset, state.isEnabled);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<{ equalizer: EqualizerState }>,
    private equalizerService: EqualizerService
  ) {}

  private readCustomPresets(): EqualizerPreset[] {
    const stored = BrowserStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      return [];
    }
    try {
      const presets = JSON.parse(stored);
      return Array.isArray(presets) ? presets : [];
    } catch (error) {
      console.error('Error loading equalizer presets:', error);
      return [];
    }
  }

  private readSettings(): StoredEqualizerSettings | null {
    const stored = BrowserStorage.getItem(this.SETTINGS_KEY);
    if (!stored) {
      return null;
    }
    try {
      const settings = JSON.parse(stored);
      if (!settings || typeof settings.presetId !== 'string') {
        return null;
      }
      return {
        presetId: settings.presetId,
        bands: Array.isArray(settings.bands) ? settings.bands : [],
        isEnabled: settings.isEnabled !== false
      };
    } catch (error) {
      console.error('Error loading equalizer settings:', error);
      return null;
    }
  }
}
